import { ApiError, type ApiClient, type QueryValue } from '@platform/api-client';
import type {
  AdminIntegration,
  CatalogEntry,
  CreateCatalogEntry,
  CreatePlanRequest,
  CreateTenantRequest,
  DeadLetterJob,
  ErrorGroup,
  FeatureKey,
  IntegrationCall,
  IntegrationHealthCheck,
  InvitePlatformUserRequest,
  ModerateFeedbackRequest,
  PendingPromotion,
  PendingPromotionRow,
  Plan,
  PlatformAuditRow,
  PlatformFeedbackItem,
  PlatformHealth,
  PlatformSettings,
  PlatformSettingsPatch,
  FileSummary,
  PlatformTenantBranding,
  PlatformUploadUrlRequest,
  PlatformUserDetail,
  PlatformUserSummary,
  QueueSummary,
  RequestRange,
  RequestSummary,
  SaveBrandingRequest,
  TenantDetail,
  TenantDomainDetail,
  TenantFeature,
  TenantHealthRow,
  TenantInstall,
  TenantSummary,
  TenantUsageRow,
  UpdateCatalogEntry,
  UpdateTenantRequest,
  UploadTicket,
} from '@platform/contracts';

import type {
  AuditQuery,
  InstallProbeResult,
  FeedbackQuery,
  IntegrationCallFilters,
  Page,
  PlatformClient,
  PlatformUserListQuery,
} from './platform';

/**
 * `PlatformClient` over the real API (MASTER_PLAN M12.7 on M12.1-M12.4, M12.6, M3.2/M3.3).
 *
 * One method, one endpoint from `Docs/API.md` §2.13, with the `{ data }` envelope
 * unwrapped (§1.2) and cursor paging passed through opaque (§1.7). The result types are
 * `@platform/contracts`', which is what the API validates its own responses against, so
 * this file adds no shape of its own.
 *
 * ## Typed helpers where the document allows one
 *
 * A path with **no parameters** - `/api/v1/platform/tenants`, `/plans`, `/users`,
 * `/settings`, `/health`, the five `/logs/*` reads - is a literal key in the generated
 * OpenAPI document, so the read goes through `api.get()` and a typo is a compile error.
 * The rest use `api.request()`, the documented escape hatch, for two reasons the
 * generator makes unavoidable:
 *
 * - **Parameterized paths** are template keys (`/tenants/{tenantId}`) and cannot be built
 *   by interpolation, so `api.get()` cannot name them.
 * - **Writes have no declared body.** Nest declares `@Body() body: unknown`, so every
 *   platform operation emits `requestBody?: never` and `BodyOf<…>` resolves to
 *   `undefined`; a typed `post`/`put`/`patch` could not carry the payload the endpoint
 *   requires. **TODO(M13.1):** `@ApiBody` on the platform controllers turns these into
 *   typed calls with no change here beyond the verb helper.
 *
 * ## Every method reaches the API
 *
 * `addDomain`, `removeDomain` and `commentOnFeedback` used to be taken from the mock,
 * because `DomainsModule` mounted only the platform list and verify and comments were
 * tenant-side. All three are mounted now (API.md §2.13, "Added while wiring the super
 * console"), so nothing on this seam is a fixture and the client takes no second
 * implementation to fill holes.
 *
 * ## Step-up and 201
 *
 * Neither is handled here. Step-up is the **call site's** business
 * (`useStepUp().guard(work)` re-runs the whole call after verification, reason included),
 * and `api.request()` already accepts any 2xx, so a `201` from a `POST` is a success like
 * any other.
 */

const PLATFORM = '/api/v1/platform';

/** Drops the screens' `'all'` sentinel and empty strings, so no `status=all` is sent. */
function filter(value: string | null | undefined): string | undefined {
  if (value === undefined || value === null || value === '' || value === 'all') return undefined;
  return value;
}

const uri = encodeURIComponent;

/** `/platform/tenants/:tenantId/installs/:key`, both segments encoded. */
function installPath(tenantId: string, integrationKey: string): string {
  return `${PLATFORM}/tenants/${uri(tenantId)}/installs/${uri(integrationKey)}`;
}

/**
 * A 2xx that is not the documented envelope is a bug worth naming rather than a
 * `Cannot read properties of undefined` three components later (API.md §1.2).
 */
function unwrap<Body, Result>(body: Body, select: (body: Body) => Result): Result {
  if (body === null || typeof body !== 'object' || !('data' in body)) {
    throw new ApiError({
      status: 200,
      code: 'UNEXPECTED_RESPONSE',
      message: 'The server sent something we did not understand. Please try again.',
      correlationId: null,
    });
  }
  return select(body);
}

/** A list answer, with the page block the API always sends beside it. */
function page<Row>(body: {
  data: Row[];
  page: { nextCursor: string | null; limit: number };
}): Page<Row> {
  return unwrap(body, (payload) => ({ data: payload.data, page: payload.page }));
}

/**
 * `doFetch` is the storage PUT's transport, injectable for tests. The PUT is not an API
 * call: `uploadUrl` is a bearer credential for one object on the storage host, so it goes
 * through plain `fetch` with `credentials: 'omit'` — the operator's session cookie has no
 * business on a storage origin — and the ticket's headers are sent verbatim, because a
 * signature computed over them fails if one is dropped (same as the clinic admin's
 * `lib/api/files.ts`).
 */
export function createPlatformApiClient(
  api: ApiClient,
  doFetch: typeof globalThis.fetch = (...args) => globalThis.fetch(...args),
): PlatformClient {
  return {
    /* ---------------------------------------------------------------- tenants */

    listTenants: async (query = {}) => {
      const body = await api.get('/api/v1/platform/tenants', {
        query: {
          q: filter(query.q),
          status: filter(query.status),
          planId: filter(query.planId),
          limit: query.limit ?? 50,
          cursor: query.cursor ?? undefined,
        },
      });
      return page<TenantSummary>(body);
    },

    getTenant: async (tenantId) => {
      const body = await api.request<{ data: TenantDetail }>(
        'get',
        `${PLATFORM}/tenants/${uri(tenantId)}`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /*
     * `adminEmail` is passed through when the dialog collected one. The endpoint's own
     * answer decides what the screen says about an invitation: as built it is `null`
     * whether or not an address was sent, and reporting "invited" from the request would
     * be telling an operator a person has mail waiting when nobody does.
     */
    createTenant: async (input: CreateTenantRequest) => {
      const body = await api.request<{
        data: {
          tenant: TenantSummary;
          invitation: { id: string; email: string; expiresAt: string } | null;
        };
      }>('post', `${PLATFORM}/tenants`, input);
      return unwrap(body, (payload) => payload.data);
    },

    updateTenant: async (tenantId, patch: UpdateTenantRequest) => {
      const body = await api.request<{ data: TenantSummary }>(
        'patch',
        `${PLATFORM}/tenants/${uri(tenantId)}`,
        patch,
      );
      return unwrap(body, (payload) => payload.data);
    },

    setTenantSuspended: async (tenantId, input) => {
      const body = await api.request<{ data: TenantSummary }>(
        'post',
        `${PLATFORM}/tenants/${uri(tenantId)}/suspend`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    archiveTenant: async (tenantId, input) => {
      const body = await api.request<{ data: TenantSummary }>(
        'post',
        `${PLATFORM}/tenants/${uri(tenantId)}/archive`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* ---------------------------------------------------------------- domains */

    listDomains: async (tenantId) => {
      const body = await api.request<{ data: TenantDomainDetail[] }>(
        'get',
        `${PLATFORM}/tenants/${uri(tenantId)}/domains`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    verifyDomain: async (tenantId, domainId) => {
      const body = await api.request<{ data: TenantDomainDetail }>(
        'post',
        `${PLATFORM}/tenants/${uri(tenantId)}/domains/${uri(domainId)}/verify`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    addDomain: async (tenantId, input) => {
      const body = await api.request<{ data: TenantDomainDetail }>(
        'post',
        `${PLATFORM}/tenants/${uri(tenantId)}/domains`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* `204`, so there is nothing to unwrap - and nothing to hand back but the absence of
       a refusal. The screen re-reads the list rather than guessing at the new one. */
    removeDomain: async (tenantId, domainId) => {
      await api.request<void>(
        'delete',
        `${PLATFORM}/tenants/${uri(tenantId)}/domains/${uri(domainId)}`,
      );
    },

    /* --------------------------------------------------- branding and features */

    saveBranding: async (tenantId, input: SaveBrandingRequest) => {
      /* The request is `.strict()` (M17.1). The branding tab builds it from the form's
         output and never sends `logoUrl` / `faviconUrl` / `contrast`, but a caller that
         hands back a whole response object must not turn a save into a 400, so the three
         read-only fields are dropped here as well. */
      const {
        logoUrl: _logoUrl,
        faviconUrl: _faviconUrl,
        contrast: _contrast,
        ...patch
      } = input as SaveBrandingRequest & Partial<PlatformTenantBranding>;
      const body = await api.request<{ data: PlatformTenantBranding }>(
        'put',
        `${PLATFORM}/tenants/${uri(tenantId)}/branding`,
        patch,
      );
      return unwrap(body, (payload) => payload.data);
    },

    uploadTenantFile: async (tenantId, kind, file) => {
      const base = `${PLATFORM}/tenants/${uri(tenantId)}/files`;
      const request: PlatformUploadUrlRequest = {
        kind,
        contentType: file.type as PlatformUploadUrlRequest['contentType'],
        sizeBytes: file.size,
        ...(file.name === '' ? {} : { fileName: file.name }),
      };
      const ticketBody = await api.request<{ data: UploadTicket }>(
        'post',
        `${base}/upload-url`,
        request,
      );
      const ticket = unwrap(ticketBody, (payload) => payload.data);

      let response: Response;
      try {
        response = await doFetch(ticket.uploadUrl, {
          method: ticket.method,
          headers: ticket.headers,
          body: file,
          credentials: 'omit',
        });
      } catch {
        throw new ApiError({
          status: 0,
          code: 'NETWORK_ERROR',
          message: 'We could not reach the file store. Check your connection and try again.',
          correlationId: null,
        });
      }
      if (!response.ok) {
        /* The storage host answers in its own dialect; none of it is for this screen. */
        throw new ApiError({
          status: response.status,
          code: 'UPLOAD_FAILED',
          message: 'That upload did not finish. Please try again.',
          correlationId: null,
        });
      }

      const completed = await api.request<{ data: { file: FileSummary } }>(
        'post',
        `${base}/${uri(ticket.fileId)}/complete`,
        {},
      );
      const stored = unwrap(completed, (payload) => payload.data.file);
      return { fileId: stored.id, url: null };
    },

    listTenantFeatures: async (tenantId) => {
      const body = await api.request<{ data: TenantFeature[] }>(
        'get',
        `${PLATFORM}/tenants/${uri(tenantId)}/features`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    setTenantFeature: async (tenantId, featureKey: FeatureKey, enabled) => {
      const body = await api.request<{ data: TenantFeature[] }>(
        'put',
        `${PLATFORM}/tenants/${uri(tenantId)}/features/${uri(featureKey)}`,
        { enabled },
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* --------------------------------------------------------------- installs */

    listInstalls: async (tenantId) => {
      const body = await api.request<{ data: TenantInstall[] }>(
        'get',
        `${PLATFORM}/tenants/${uri(tenantId)}/installs`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    enableInstall: async (tenantId, integrationKey) => {
      const body = await api.request<{ data: TenantInstall }>(
        'post',
        `${PLATFORM}/tenants/${uri(tenantId)}/installs/${uri(integrationKey)}/enable`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    disableInstall: async (tenantId, integrationKey, input) => {
      const body = await api.request<{ data: TenantInstall }>(
        'post',
        `${PLATFORM}/tenants/${uri(tenantId)}/installs/${uri(integrationKey)}/disable`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* M16.2 (ADR-033): one install in full, under the tenant's own path. */

    getInstallDetail: async (tenantId, integrationKey) => {
      const body = await api.request<{ data: AdminIntegration }>(
        'get',
        installPath(tenantId, integrationKey),
      );
      return unwrap(body, (payload) => payload.data);
    },

    putInstallCredentials: async (tenantId, integrationKey, input) => {
      const body = await api.request<{ data: InstallProbeResult }>(
        'put',
        `${installPath(tenantId, integrationKey)}/credentials`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    deleteInstallCredentials: async (tenantId, integrationKey) => {
      const body = await api.request<{ data: AdminIntegration }>(
        'delete',
        `${installPath(tenantId, integrationKey)}/credentials`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    patchInstallSettings: async (tenantId, integrationKey, settings) => {
      const body = await api.request<{ data: AdminIntegration }>(
        'patch',
        `${installPath(tenantId, integrationKey)}/settings`,
        { settings },
      );
      return unwrap(body, (payload) => payload.data);
    },

    testInstall: async (tenantId, integrationKey) => {
      const body = await api.request<{ data: InstallProbeResult }>(
        'post',
        `${installPath(tenantId, integrationKey)}/test`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    listInstallCalls: async (tenantId, integrationKey, query = {}) => {
      const body = await api.request<{
        data: IntegrationCall[];
        page: { nextCursor: string | null; limit: number };
      }>('get', `${installPath(tenantId, integrationKey)}/calls`, undefined, {
        query: {
          status: query.status,
          operation: query.operation,
          limit: query.limit ?? 20,
          cursor: query.cursor ?? undefined,
        },
      });
      return page(body);
    },

    listInstallHealth: async (tenantId, integrationKey, query = {}) => {
      const body = await api.request<{
        data: IntegrationHealthCheck[];
        page: { nextCursor: string | null; limit: number };
      }>('get', `${installPath(tenantId, integrationKey)}/health`, undefined, {
        query: { limit: query.limit ?? 10, cursor: query.cursor ?? undefined },
      });
      return page(body);
    },

    /* ------------------------------------------------------------------ plans */

    /*
     * `request`, not `get`: the generated document types `features` as an index signature,
     * which is not assignable to `Record<FeatureKey, boolean>` - the contract's promise
     * that **every** key is present, which is what lets the matrix render without holes.
     * The contract type is the stricter and the truer of the two.
     */
    listPlans: async () => {
      const body = await api.request<{ data: Plan[] }>('get', `${PLATFORM}/plans`);
      return unwrap(body, (payload) => payload.data);
    },

    createPlan: async (input: CreatePlanRequest) => {
      const body = await api.request<{ data: Plan }>('post', `${PLATFORM}/plans`, input);
      return unwrap(body, (payload) => payload.data);
    },

    /*
     * One cell, as the endpoint spells it: `features` is a *partial* matrix and only the
     * rows named are written, so sending the whole plan back would make two operators
     * editing different cells overwrite each other (API.md §2.13).
     */
    setPlanFeature: async (planId, featureKey: FeatureKey, enabled) => {
      const body = await api.request<{ data: Plan }>('patch', `${PLATFORM}/plans/${uri(planId)}`, {
        features: [{ featureKey, enabled, limits: {} }],
      });
      return unwrap(body, (payload) => payload.data);
    },

    /* ---------------------------------------------------------------- catalog */

    listCatalog: async () => {
      const body = await api.get('/api/v1/platform/integrations');
      return unwrap(body, (payload): CatalogEntry[] => payload.data);
    },

    createCatalogEntry: async (input: CreateCatalogEntry) => {
      const body = await api.request<{ data: CatalogEntry }>(
        'post',
        `${PLATFORM}/integrations`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    updateCatalogEntry: async (key, patch: UpdateCatalogEntry) => {
      const body = await api.request<{ data: CatalogEntry }>(
        'patch',
        `${PLATFORM}/integrations/${uri(key)}`,
        patch,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* ------------------------------------------------------------------ users */

    listUsers: async (query: PlatformUserListQuery = {}) => {
      const body = await api.get('/api/v1/platform/users', {
        query: {
          q: filter(query.q),
          population: filter(query.population),
          tenantId: filter(query.tenantId),
          status: filter(query.status),
          mfa: filter(query.mfa),
          limit: query.limit ?? 50,
          cursor: query.cursor ?? undefined,
        },
      });
      return page<PlatformUserSummary>(body);
    },

    getUser: async (userId) => {
      const body = await api.request<{ data: PlatformUserDetail }>(
        'get',
        `${PLATFORM}/users/${uri(userId)}`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    revokeUserSessions: async (userId) => {
      const body = await api.request<{ data: PlatformUserDetail }>(
        'post',
        `${PLATFORM}/users/${uri(userId)}/sessions/revoke-all`,
      );
      return unwrap(body, (payload) => payload.data);
    },

    resetUserMfa: async (userId, input) => {
      const body = await api.request<{ data: PlatformUserDetail }>(
        'post',
        `${PLATFORM}/users/${uri(userId)}/mfa/reset`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    setUserActive: async (userId, input) => {
      const body = await api.request<{ data: PlatformUserSummary }>(
        'post',
        `${PLATFORM}/users/${uri(userId)}/active`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    promoteUser: async (userId, input) => {
      const body = await api.request<{
        data: { user: PlatformUserSummary; pending: PendingPromotion | null };
      }>('post', `${PLATFORM}/users/${uri(userId)}/promote`, input);
      return unwrap(body, (payload) => payload.data);
    },

    confirmPromotion: async (userId, input) => {
      const body = await api.request<{
        data: { user: PlatformUserSummary; pending: PendingPromotion | null };
      }>('post', `${PLATFORM}/users/${uri(userId)}/promote/confirm`, input);
      return unwrap(body, (payload) => payload.data);
    },

    /*
     * `/platform/promotions` has no path parameter, so it is a literal key in the
     * generated document and goes through the typed `api.get()`.
     */
    listPendingPromotions: async (query = {}) => {
      const body = await api.get('/api/v1/platform/promotions', {
        query: {
          status: query.status ?? 'pending',
          limit: query.limit ?? 50,
          cursor: query.cursor ?? undefined,
        },
      });
      return page<PendingPromotionRow>(body);
    },

    demoteUser: async (userId, input) => {
      const body = await api.request<{
        data: { user: PlatformUserSummary; pending: PendingPromotion | null };
      }>('post', `${PLATFORM}/users/${uri(userId)}/demote`, input);
      return unwrap(body, (payload) => payload.data);
    },

    invitePlatformUser: async (input: InvitePlatformUserRequest) => {
      const body = await api.request<{ data: PlatformUserSummary }>(
        'post',
        `${PLATFORM}/users`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* ------------------------------------------------------------------- logs */

    listAudit: async (query: AuditQuery = {}) => {
      const body = await api.get('/api/v1/platform/logs/audit', {
        query: {
          tenantId: filter(query.tenantId),
          actorId: filter(query.actorId),
          action: filter(query.action),
          from: filter(query.from),
          to: filter(query.to),
          limit: query.limit ?? 25,
          cursor: query.cursor ?? undefined,
        },
      });
      return page<PlatformAuditRow>(body);
    },

    listIntegrationCalls: async (query: IntegrationCallFilters = {}) => {
      const body = await api.get('/api/v1/platform/integrations/calls', {
        query: {
          tenantId: filter(query.tenantId),
          integrationKey: filter(query.integrationKey),
          status: filter(query.status),
          from: filter(query.from),
          to: filter(query.to),
          limit: query.limit ?? 25,
          cursor: query.cursor ?? undefined,
        },
      });
      return page<IntegrationCall>(body);
    },

    listQueues: async () => {
      const body = await api.get('/api/v1/platform/logs/jobs');
      return unwrap(body, (payload): QueueSummary[] => payload.data);
    },

    listDeadLetters: async (query = {}) => {
      const search: Record<string, QueryValue> = {
        queue: filter(query.queue),
        limit: query.limit ?? 50,
      };
      const body = await api.get('/api/v1/platform/logs/jobs/dead-letters', { query: search });
      return unwrap(body, (payload): DeadLetterJob[] => payload.data);
    },

    redriveJob: async (jobId, input) => {
      const body = await api.request<{ data: DeadLetterJob[] }>(
        'post',
        `${PLATFORM}/logs/jobs/dead-letters/${uri(jobId)}/redrive`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    discardJob: async (jobId, input) => {
      const body = await api.request<{ data: DeadLetterJob[] }>(
        'post',
        `${PLATFORM}/logs/jobs/dead-letters/${uri(jobId)}/discard`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    getRequestSummary: async (range: RequestRange) => {
      const body = await api.get('/api/v1/platform/logs/requests', { query: { range } });
      return unwrap(body, (payload): RequestSummary => payload.data);
    },

    listErrorGroups: async (range: RequestRange) => {
      const body = await api.get('/api/v1/platform/logs/errors', { query: { range } });
      return unwrap(body, (payload): ErrorGroup[] => payload.data);
    },

    /* --------------------------------------------------------------- settings */

    getSettings: async () => {
      const body = await api.get('/api/v1/platform/settings');
      return unwrap(body, (payload): PlatformSettings => payload.data);
    },

    /*
     * One patch, not a write per key: the console edits a form and saves it, and five
     * requests that can half-succeed is not a save (API.md §2.13, note 2).
     */
    saveSettings: async (patch: PlatformSettingsPatch) => {
      const body = await api.request<{ data: PlatformSettings }>(
        'patch',
        `${PLATFORM}/settings`,
        patch,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* --------------------------------------------------------------- feedback */

    listFeedback: async (query: FeedbackQuery = {}) => {
      const body = await api.get('/api/v1/platform/feedback', {
        query: {
          /* `'platform'` means "the rows with no tenant", which the endpoint expresses by
             the absence of a `tenantId` - there is no id for "not a clinic". */
          tenantId: query.tenantId === 'platform' ? undefined : filter(query.tenantId),
          category: filter(query.category),
          status: filter(query.status),
          visibility: filter(query.visibility),
          limit: query.limit ?? 50,
          cursor: query.cursor ?? undefined,
        },
      });
      const answered = page<PlatformFeedbackItem>(body);
      if (query.tenantId !== 'platform') return answered;
      return { ...answered, data: answered.data.filter((item) => item.tenantId === null) };
    },

    updateFeedback: async (itemId, patch: ModerateFeedbackRequest) => {
      const body = await api.request<{ data: PlatformFeedbackItem }>(
        'patch',
        `${PLATFORM}/feedback/${uri(itemId)}`,
        patch,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* The endpoint answers the whole item, so the board re-renders from one response. */
    commentOnFeedback: async (itemId, input) => {
      const body = await api.request<{ data: PlatformFeedbackItem }>(
        'post',
        `${PLATFORM}/feedback/${uri(itemId)}/comments`,
        input,
      );
      return unwrap(body, (payload) => payload.data);
    },

    /* ----------------------------------------------------------------- health */

    getPlatformHealth: async () => {
      const body = await api.get('/api/v1/platform/health');
      return unwrap(body, (payload): PlatformHealth => payload.data);
    },

    listTenantHealth: async () => {
      const body = await api.get('/api/v1/platform/health/tenants');
      return unwrap(body, (payload): TenantHealthRow[] => payload.data);
    },

    getUsageReport: async (query = {}) => {
      const body = await api.get('/api/v1/platform/reports/usage', {
        query: {
          from: filter(query.from),
          to: filter(query.to),
          tenantId: filter(query.tenantId),
        },
      });
      return unwrap(
        body,
        (payload): { range: { from: string; to: string }; tenants: TenantUsageRow[] } =>
          payload.data,
      );
    },
  };
}
